import { resolveTileCoverDisplayUrl } from '../utils/tileCoverArt';
import { finishGridEntryPhase, markGridEntryPhaseStart } from '../utils/gridEntryPerf';

/** Small bound — album-wall tiles must share this queue (never unbounded fetch). */
export const VIEWPORT_COVER_MAX_CONCURRENT = 4;
const MAX_CONCURRENT = VIEWPORT_COVER_MAX_CONCURRENT;
const resolvedCache = new Map();

let inFlight = 0;
const waiters = [];
let viewportGeneration = 0;
let firstResolutionWaveStart = 0;
let firstResolutionWaveDone = false;
/** Bumped on mount / retention-restore / visible-window settle so tiles re-enqueue. */
let coverPrimeGeneration = 0;
const coverPrimeListeners = new Set();

export function beginViewportFirstResolutionWaveTiming() {
  if (firstResolutionWaveDone) return;
  firstResolutionWaveStart = markGridEntryPhaseStart();
}

function maybeFinishFirstResolutionWave() {
  if (firstResolutionWaveDone || !firstResolutionWaveStart) return;
  firstResolutionWaveDone = true;
  finishGridEntryPhase('first_resolution_wave', firstResolutionWaveStart);
  firstResolutionWaveStart = 0;
}

/** Cancel pending viewport resolves when scroll moves past tiles. */
export function bumpViewportCoverGeneration() {
  viewportGeneration += 1;
  while (waiters.length > 0) {
    const job = waiters.pop();
    if (!job) break;
    job.cancelled = true;
    job.resolve(null);
  }
}

export function getViewportCoverPrimeGeneration() {
  return coverPrimeGeneration;
}

/** Subscribe to mount / retention-restore / visible-window primes. */
export function subscribeViewportCoverPrime(listener) {
  coverPrimeListeners.add(listener);
  return () => coverPrimeListeners.delete(listener);
}

/**
 * Signal visible tiles to (re)enqueue cover resolution.
 * Call on grid mount, visible-window settle, and when a retained
 * display:none wall becomes visible again — scroll alone must not be
 * the only demand path.
 */
export function primeViewportCoverResolution(reason = 'prime') {
  coverPrimeGeneration += 1;
  const gen = coverPrimeGeneration;
  for (const listener of coverPrimeListeners) {
    try {
      listener(gen, reason);
    } catch {
      /* tile listeners must not break the prime fan-out */
    }
  }
  return gen;
}

function coverAlbumKey(album) {
  if (!album) return '';
  return [
    album.cover_art_thumb_hash,
    album.embedded_cover_hash,
    album.folder_cover_hash,
    album.album_key || album.id,
    album.cover_art_url,
  ].join(':');
}

async function resolveAlbumCover(album, generation) {
  if (!album) return null;
  if (generation !== viewportGeneration) return null;
  const key = coverAlbumKey(album);
  if (resolvedCache.has(key)) return resolvedCache.get(key);

  const url = await resolveTileCoverDisplayUrl(album);
  if (generation !== viewportGeneration) return null;
  resolvedCache.set(key, url);
  // Only close the wave on a real URL — null/miss used to finish in ~1ms
  // and hide the "processed nothing" demand failure mode.
  if (url) maybeFinishFirstResolutionWave();
  return url;
}

function pumpQueue() {
  while (inFlight < MAX_CONCURRENT && waiters.length > 0) {
    const job = waiters.shift();
    if (!job || job.cancelled) continue;
    inFlight += 1;
    const work = typeof job.work === 'function'
      ? job.work()
      : resolveAlbumCover(job.album, job.generation);
    Promise.resolve(work)
      .then((url) => { if (!job.cancelled) job.resolve(url); })
      .catch(() => { if (!job.cancelled) job.resolve(null); })
      .finally(() => {
        inFlight -= 1;
        pumpQueue();
      });
  }
}

/** Enqueue one album cover resolve — bounded concurrency, shared result cache. */
export function enqueueAlbumCoverResolve(album) {
  const key = coverAlbumKey(album);
  if (resolvedCache.has(key)) {
    return Promise.resolve(resolvedCache.get(key));
  }
  const generation = viewportGeneration;
  return new Promise((resolve) => {
    waiters.push({ album, resolve, cancelled: false, generation });
    pumpQueue();
  });
}

/**
 * Enqueue arbitrary cover work (album-grid hash/thumb resolve) through the
 * same concurrency cap so scan-time tiles cannot stampede IPC.
 */
export function enqueueCoverWork(cacheKey, work) {
  const key = String(cacheKey || '');
  if (key && resolvedCache.has(key)) {
    return Promise.resolve(resolvedCache.get(key));
  }
  const generation = viewportGeneration;
  return new Promise((resolve) => {
    waiters.push({
      album: null,
      work: async () => {
        const url = await work();
        if (key && generation === viewportGeneration && url) {
          resolvedCache.set(key, url);
        }
        return url;
      },
      resolve,
      cancelled: false,
      generation,
    });
    pumpQueue();
  });
}

/** Resolve up to `max` covers sequentially through the shared queue. */
export async function resolveCollageCovers(albums, max = 4) {
  const urls = [];
  for (const album of (albums || []).slice(0, max)) {
    const url = await enqueueAlbumCoverResolve(album);
    if (url) urls.push(url);
  }
  return urls;
}

/** Test helper — count queue jobs fired (not cache hits). */
export function resetViewportCoverQueueForTests() {
  resolvedCache.clear();
  waiters.length = 0;
  inFlight = 0;
  viewportGeneration = 0;
  firstResolutionWaveStart = 0;
  firstResolutionWaveDone = false;
  coverPrimeGeneration = 0;
}

export function getViewportCoverQueueStats() {
  return { inFlight, queued: waiters.length, cached: resolvedCache.size };
}

export function getViewportCoverGenerationForTests() {
  return viewportGeneration;
}
